import React from "react";
import _ from 'lodash';

import {parseResponse} from "../../../utils/api";
import {generateQueryParamsHash, useSharedDataContext} from "../utils";
import {useTabsSharedDataContext} from "../components_v2/TabsSharedData/TabsSharedData";
import {isCancel, useCancelableRequest} from "../../../hooks/useCancelableRequest";

const SHARED_DATA_KEY = 'extended-assets-data';

export function useExtendedAssetsData(customerId, portfolio, dataProvider) {

  const dataContext = useSharedDataContext();
  const sharedData = useTabsSharedDataContext();
  const getCancelToken = useCancelableRequest();
  const ptfData = dataContext.getPortfolioData(portfolio.depotNumber);

  const fetch = async () => {
    const cancelToken = getCancelToken();

    const _setState = (depotNr, depotData, onlyCache=false) => {
      // It is not possible to sync data with shared data in
      // hook because hook is not executed if component destroyed
      if(!onlyCache) dataContext.setPortfolioData(depotNr, depotData);
      sharedData.setDataValue(`${SHARED_DATA_KEY}-${portfolio.depotNumber}`, hashKey, depotData);
    };

    const hashKey = generateQueryParamsHash(customerId, [portfolio]);

    if(dataContext.isPortfolioDataLoading(portfolio.depotNumber)){
      return;
    }
    dataContext.setPortfolioDataLoading(portfolio.depotNumber, true);
    _setState(portfolio.depotNumber, {
      data: null,
      loading: true,
      errors: null,
      updatedAt: +new Date()
    });

    try {
      const response = await dataProvider.getPortfolioExtendedAssetData(
        customerId, portfolio.depotNumberForAssets || portfolio.depotNumber, undefined, undefined,
        cancelToken
      );
      parseResponse(response, 'extended_asset_data', (data) => {
        _setState(portfolio.depotNumber, {
          data: data,
          loading: false,
          errors: null,
          updatedAt: +new Date()
        })
      }, (errors) => {
        _setState(portfolio.depotNumber, {
          data: null,
          loading: false,
          errors: errors,
          updatedAt: +new Date()
        })
      }, false)
    } catch (errors) {
      _setState(portfolio.depotNumber, {
        data: null,
        loading: false,
        errors: errors,
        updatedAt: +new Date()
      }, isCancel(errors))
    }

    dataContext.setPortfolioDataLoading(portfolio.depotNumber, false);
  };

  const syncFromShared = () => {
    const sharedPortfolioData = !!sharedData
        ? sharedData.getDataValue(`${SHARED_DATA_KEY}-${portfolio.depotNumber}`, generateQueryParamsHash(customerId, [portfolio]))
        : undefined;

      // fetch data if there is no shared data or shared data was loaded with error
      if (!!sharedPortfolioData && !sharedPortfolioData.errors && sharedPortfolioData.updatedAt !== ptfData.updatedAt) {
        // sync internal hook data with shared data.
        dataContext.setPortfolioData(portfolio.depotNumber, sharedPortfolioData);

        return true
      } else {
        return false
      }
  };

  React.useEffect(() => {syncFromShared()}, [_.get(sharedData, 'updatedAt')]);

  React.useEffect(() => {
    if (!!customerId && !!portfolio) {
      const synced = syncFromShared();

      // fetch data if there is no shared data or shared data was loaded with error
      if (!synced) {
        fetch();
      }
    }
  }, [customerId, _.get(portfolio, 'depotNumberForAssets'), _.get(portfolio, 'depotNumber')]);

  return ptfData
}